/**
 * Sorts an array of objects based on a numeric property.
 * Null, undefined, or non-numeric values are treated like zero values.
 *
 * @param array - The array of objects to sort.
 * @param property - The key of the numeric property to sort by.
 * @param order - The sorting order, either 'asc' (ascending) or 'desc' (descending)
 * @returns A new array sorted by the specified numeric property.
 */
export function sortByNumericProperty<T>(
  array: T[],
  property: keyof T,
  order: 'asc' | 'desc',
): T[] {
  return [...array].sort((a, b) => {
    const aValue = a[property] || 0;
    const bValue = b[property] || 0;

    // Compare numeric values
    const aNumeric = Number(aValue);
    const bNumeric = Number(bValue);

    return order === 'asc' ? aNumeric - bNumeric : bNumeric - aNumeric;
  });
}

/**
 * Sorts an array of objects based on a numeric property.
 * Null, undefined, or non-numeric values are always placed at the end of the result array.
 *
 * @param array - The array of objects to sort.
 * @param property - The key of the numeric property to sort by.
 * @param order - The sorting order, either 'asc' (ascending) or 'desc' (descending)
 * @returns A new array sorted by the specified numeric property.
 */
export function sortByNumericPropertyWithNullsLast<T>(
  array: T[],
  property: keyof T,
  order: 'asc' | 'desc',
): T[] {
  return [...array].sort((a, b) => {
    const aValue = a[property];
    const bValue = b[property];

    // Handle null, undefined, or non-numeric values
    const aIsInvalid = aValue == null || isNaN(Number(aValue));
    const bIsInvalid = bValue == null || isNaN(Number(bValue));

    if (aIsInvalid && bIsInvalid) {
      return 0; // Both invalid, keep relative order
    }
    if (aIsInvalid) {
      return 1; // Place `a` after `b`
    }
    if (bIsInvalid) {
      return -1; // Place `b` after `a`
    }

    // Compare numeric values
    const aNumeric = Number(aValue);
    const bNumeric = Number(bValue);

    return order === 'asc' ? aNumeric - bNumeric : bNumeric - aNumeric;
  });
}

/**
 * Sorts an array of objects based on a string property.
 * For nullish values, a substitute string value can be specified to be
 * used for comparisons, otherwise an empty string is used by default.
 *
 * @param array - The array of objects to sort.
 * @param property - The key of the property to sort by.
 * @param order - The sorting order, either 'asc' (ascending) or 'desc' (descending)
 * @param options - Additional options:
 *  - valueForNulls: a string to use for sorting comparison in place of nullish values
 * @returns A new array sorted by the specified string property.
 */
export function sortByStringProperty<T>(
  array: T[],
  property: keyof T,
  order: 'asc' | 'desc',
  options?: {
    valueForNulls?: string;
  },
): T[] {
  return [...array].sort((a, b) => {
    const valueA = a[property] || options?.valueForNulls || '';
    const valueB = b[property] || options?.valueForNulls || '';

    // Compare string values
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      if (order === 'asc') {
        return valueA.localeCompare(valueB);
      } else {
        return valueB.localeCompare(valueA);
      }
    }

    return 0;
  });
}

/**
 * Sorts an array of objects based on a string property.
 * Null or undefined values are always placed at the end of the result array.
 *
 * @param array - The array of objects to sort.
 * @param property - The key of the property to sort by.
 * @param order - The sorting order, either 'asc' (ascending) or 'desc' (descending)
 * @returns A new array sorted by the specified string property.
 */
export function sortByStringPropertyWithNullsLast<T>(
  array: T[],
  property: keyof T,
  order: 'asc' | 'desc',
): T[] {
  return [...array].sort((a, b) => {
    const valueA = a[property];
    const valueB = b[property];

    // Handle null or undefined values
    if (valueA == null && valueB == null) {
      return 0;
    }
    if (valueA == null) {
      return 1;
    }
    if (valueB == null) {
      return -1;
    }

    // Compare string values
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      if (order === 'asc') {
        return valueA.localeCompare(valueB);
      } else {
        return valueB.localeCompare(valueA);
      }
    }

    return 0;
  });
}
